<template>
  <div class="v2-order-products">
    <template v-if="isLoaded">
      <!-- KP-4363: search the products of this order -->
      <div
        v-if="isProductSearchEnabled"
        class="v2-order-products__search"
      >
        <v2-input
          v-model="productSearch"
          type="search"
          placeholder="Search products in this order"
          aria-label="Search products in this order"
        />
      </div>

      <!-- Ship controls -->
      <div
        v-if="order && order.isEnableShipmentFeature && order.readyToShipProductsCount && !isShipmentView"
        class="v2-order-products__ship-controls"
      >
        <label
          v-if="order.readyToShipProductsCount > 1 || isPartiallyConfirmedOrder(order)"
          class="v2-order-products__select-all"
        >
          <input
            type="checkbox"
            :checked="isSelectedAll(order)"
            @change="onSelectReadyToShipProducts($event.target.checked, order)"
          >
          <span>{{ getShipSelectAllLabel(order) }}</span>
        </label>
        <v2-button
          variant="primary"
          :disabled="!isSelectedProduct"
          @click="shipProducts(order, emitShipNow)"
        >
          Ship Now
        </v2-button>
      </div>

      <!-- Return-to-order controls (brand, shipment still in created state) -->
      <div
        v-if="isReturnToOrderEnabled"
        class="v2-order-products__ship-controls"
      >
        <label class="v2-order-products__select-all">
          <input
            type="checkbox"
            :checked="isAllSelectedForReturn"
            @change="onToggleSelectAllForReturn($event.target.checked)"
          >
          <span>Select all products</span>
        </label>
        <v2-button
          variant="primary"
          :disabled="!hasSelectedForReturn || isReturningProductsToOrder"
          @click="showReturnModal = true"
        >
          {{ isReturningProductsToOrder ? 'Moving...' : 'Move to Order' }}
        </v2-button>
      </div>

      <!-- KP-4363: select pending products to confirm -->
      <div
        v-if="hasPendingProductsToConfirm"
        class="v2-order-products__ship-controls"
      >
        <label class="v2-order-products__select-all">
          <input
            type="checkbox"
            :checked="isAllPendingSelected(visibleOrderProducts)"
            @change="toggleAllPendingForConfirm(visibleOrderProducts, $event.target.checked)"
          >
          <span>Select all pending products</span>
        </label>
      </div>

      <!-- Products list -->
      <template v-if="productsSlice.length">
        <div
          v-for="(product, index) in productsSlice"
          :key="product.productId"
          class="v2-order-products__card"
          :class="{
            'v2-order-products__card--selected':
              product.status === ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL,
            'v2-order-products__card--error': isRemoveEnabled(product, order, isBrand, isRetailer) && product.hasError,
            'v2-order-products__card--discount': hasProductDiscount(product),
          }"
        >
          <div class="v2-order-products__layout">
            <!-- Product Image -->
            <div class="v2-order-products__image">
              <button
                type="button"
                class="v2-order-products__image-trigger"
                :aria-label="`View product details: ${product.name}`"
                @click="onClickProduct(product)"
              >
                <img
                  :src="parseProductImage(product)"
                  :alt="product.name"
                  class="v2-order-products__img"
                  @error="imageLoadError"
                >
              </button>
              <span
                v-if="product.status === ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL && (product.isDeletedByRetailer || product.isDeletedByBrand)"
                class="v2-order-products__removed-label"
              >
                Removed by {{ product.isDeletedByRetailer ? 'the retailer' : 'the brand' }}
              </span>
              <span
                v-if="product.status !== ORDER_PRODUCT_SHIPMENT_STATUS.CANCELLED.LABEL && product.isEdited"
                class="v2-order-products__modified-label"
              >
                Modified by the brand
              </span>
            </div>

            <!-- Product Info -->
            <div class="v2-order-products__info">
              <div class="v2-order-products__header">
                <div>
                  <h4 class="v2-order-products__name">
                    <button
                      type="button"
                      class="v2-order-products__name-link"
                      :aria-label="`View product details: ${product.name}`"
                      @click="onClickProduct(product)"
                    >
                      {{ product.name }}
                    </button>
                  </h4>
                  <p class="v2-order-products__breadcrumb">
                    {{ [product.department, product.category, product.subCategory].filter(Boolean).join(' / ') }}
                  </p>
                </div>
                <div class="v2-order-products__status-area">
                  <v2-badge
                    v-if="isShowStatusTag(product.status)"
                    :variant="getStatusBadgeVariant(product.status)"
                  >
                    {{ getLineStatusText(product, order) }}
                  </v2-badge>
                </div>
              </div>
              <div class="v2-order-products__details">
                <div class="v2-order-products__detail-row">
                  <span>Style Code:</span>
                  <strong>{{ product.styleCode }}</strong>
                </div>
                <div class="v2-order-products__detail-row">
                  <span>Retail Price:</span>
                  <strong>{{ formatCurrency(product.retailPrice, productCurrency) }}</strong>
                </div>
                <div class="v2-order-products__detail-row">
                  <span>Wholesale Selling Price:</span>
                  <strong :class="{ 'v2-order-products__discounted': hasProductDiscount(product) }">
                    {{ formatCurrency(product.price, productCurrency) }}
                  </strong>
                </div>
                <div
                  v-if="!isRetailer && product.purchasePrice"
                  class="v2-order-products__detail-row"
                >
                  <span>Kingpin Purchase Price:</span>
                  <strong>{{ formatCurrency(product.purchasePrice, productCurrency) }}</strong>
                </div>

                <!-- Product Discount - Brand Editable -->
                <div
                  v-if="isDiscountEnabled && !isProductDiscountDisabled(product)"
                  class="v2-order-products__discount-section"
                >
                  <!-- View mode: has discount, not editing -->
                  <div
                    v-if="hasProductDiscount(product) && !isEditingDiscount(product)"
                    class="v2-order-products__discount-view"
                  >
                    <span>Discount:</span>
                    <v2-badge variant="success">
                      -{{ convertValueToPercent(product.discount) }}%
                    </v2-badge>
                    <button
                      class="v2-order-products__discount-edit-btn"
                      :disabled="isApplyingDiscount"
                      @click="startEditDiscount(product)"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </div>

                  <!-- Edit mode -->
                  <div
                    v-else-if="isEditingDiscount(product)"
                    class="v2-order-products__discount-edit"
                  >
                    <span>Discount %:</span>
                    <div class="v2-order-products__discount-controls">
                      <input
                        v-model="productDiscounts[product.productId]"
                        type="number"
                        class="v2-order-products__discount-input"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="e.g., 15"
                        :disabled="isApplyingDiscount"
                      >
                      <span>%</span>
                      <v2-button
                        v-if="productDiscounts[product.productId] !== '' && productDiscounts[product.productId] !== undefined"
                        variant="primary"
                        size="sm"
                        :disabled="isApplyingDiscount"
                        @click="handleApplyDiscount(product)"
                      >
                        Apply
                      </v2-button>
                      <v2-button
                        variant="outline"
                        size="sm"
                        :disabled="isApplyingDiscount"
                        @click="cancelEditDiscount(product)"
                      >
                        Cancel
                      </v2-button>
                    </div>
                    <small class="v2-order-products__discount-hint">Enter discount percentage (0-100)</small>
                  </div>

                  <!-- No discount yet: show edit button to add -->
                  <div
                    v-else
                    class="v2-order-products__discount-view"
                  >
                    <span>Discount:</span>
                    <button
                      class="v2-order-products__discount-edit-btn"
                      :disabled="isApplyingDiscount"
                      @click="startEditDiscount(product)"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- Product Discount - Retailer Read-Only -->
                <div
                  v-if="isRetailer && hasProductDiscount(product) && !isShipmentView"
                  class="v2-order-products__detail-row"
                >
                  <span>Discount:</span>
                  <v2-badge variant="success">
                    -{{ convertValueToPercent(product.discount) }}%
                  </v2-badge>
                </div>
              </div>

              <!-- Carton quantity -->
              <div
                v-if="product.isCarton"
                class="v2-order-products__carton-qty"
              >
                <span>Qty:</span>
                <input
                  v-model.number="product.quantity"
                  type="number"
                  class="v2-order-products__qty-input"
                  :class="{ 'v2-order-products__qty-input--invalid': isCartonExceedingShipmentQty(product, order, isShipmentView, isShipmentSettingsModal) }"
                  :disabled="isCartonInputDisabled(product, !isModifyEnabledForBrand(!isModifyEnabledProp, order), order)"
                  :max="getCartonMaxQty(product, order)"
                  min="0"
                  @input="onCartonQuantityChange(order)"
                >
                <div
                  v-if="shouldShowCartonMaxQty(product, isShipmentSettingsModal)"
                  class="v2-order-products__carton-pending"
                >
                  Pending to Ship: {{ getCartonDisplayMaxQty(product, isShipmentSettingsModal) }}
                </div>
              </div>

              <!-- Availability dates -->
              <div
                v-if="product.shippingStartDate && product.shippingEndDate"
                class="v2-order-products__detail-row"
              >
                <span>Availability Date:</span>
                <strong>{{ formattedDate(product.shippingStartDate) }} to {{ formattedDate(product.shippingEndDate) }}</strong>
              </div>

              <!-- Product Variants Table -->
              <product-variants
                :disabled-product="isProductDisabled(product, order, isShipmentView)"
                :is-carton="product.isCarton"
                :items="getProductVariantItems(product, isShipmentView)"
                :is-modify-enabled-brand="isModifyEnabledForBrand(!isModifyEnabledProp, order)"
                :is-shipment-settings-modal="isShipmentSettingsModal"
                :show-quantity-exceed-validation="shouldValidateShipmentQty(order, isShipmentView, isShipmentSettingsModal)"
                @update-variants="(variants) => updateProductVariants(variants, product, order)"
              />
            </div>

            <!-- Totals column with vertical divider -->
            <div class="v2-order-products__totals-col">
              <!-- Ship checkbox -->
              <div
                v-if="order && order.isEnableShipmentFeature"
                class="v2-order-products__totals-actions"
              >
                <label
                  v-if="isLineShipSelectable(product, order)"
                  class="v2-order-products__ship-checkbox"
                >
                  <input
                    type="checkbox"
                    :checked="product.status === ORDER_PRODUCT_SHIPMENT_STATUS.SELECTED_BY_USER.LABEL"
                    @change="onSelect($event.target.checked, product, index)"
                  >
                </label>
              </div>
              <!-- KP-4363: confirm-selection checkbox -->
              <div
                v-if="isLineSelectableForConfirm(product, order, isShipmentView)"
                class="v2-order-products__totals-actions"
              >
                <label class="v2-order-products__ship-checkbox">
                  <input
                    type="checkbox"
                    :aria-label="`Select ${product.name} to confirm`"
                    :checked="isLineSelectedForConfirm(product)"
                    @change="toggleLineForConfirm(product, $event.target.checked)"
                  >
                </label>
              </div>
              <!-- Move-to-order checkbox -->
              <div
                v-if="isReturnToOrderEnabled && !isProductDeletedOrCancelled(product)"
                class="v2-order-products__totals-actions"
              >
                <label class="v2-order-products__ship-checkbox">
                  <input
                    type="checkbox"
                    :checked="isSelectedForReturn(product)"
                    @change="onToggleReturnSelection($event.target.checked, product)"
                  >
                </label>
              </div>
              <!-- Remove button -->
              <v2-button
                v-if="isRemoveEnabled(product, order, isBrand, isRetailer)"
                variant="destructive"
                size="sm"
                class="v2-order-products__remove-btn"
                @click="showRemoveModal = product.productId"
              >
                Remove
              </v2-button>
              <!-- Totals -->
              <div
                v-if="variantLoaded"
                class="v2-order-products__totals"
              >
                <div class="v2-order-products__total-value">
                  Total Value:<br>
                  {{ formatCurrency(product.lineTotalPrice, productCurrency) }}
                </div>
                <div class="v2-order-products__total-units">
                  Total Units: {{ formatNumber(product.lineTotalQuantity || 0) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Partially shipped expand -->
          <div
            v-if="isPartiallyShipped(product) && hasShippedItems(product)"
            class="v2-order-products__partial-bar"
            @click="togglePartialBreakdown(product.productId)"
          >
            <v2-badge variant="success">
              Shipped: {{ product.shippedQuantity || 0 }}
            </v2-badge>
            <v2-badge variant="warning">
              Remaining: {{ getRemainingUnits(product) }}
            </v2-badge>
            <span class="v2-order-products__expand-text">
              {{ expandedPartialProducts[product.productId] ? 'Hide Details' : 'View Details' }}
            </span>
          </div>

          <!-- Partial shipment breakdown (expanded) -->
          <div
            v-if="expandedPartialProducts[product.productId] && isPartiallyShipped(product) && hasShippedItems(product)"
            class="v2-order-products__breakdown"
          >
            <!-- Carton products: simple quantity display -->
            <template v-if="product.isCarton">
              <div class="v2-order-products__breakdown-cartons">
                <div class="v2-order-products__breakdown-col">
                  <div class="v2-order-products__breakdown-label v2-order-products__breakdown-label--success">
                    Shipped Cartons
                  </div>
                  <div class="v2-order-products__breakdown-value v2-order-products__breakdown-value--success">
                    {{ product.shippedQuantity || 0 }}
                  </div>
                </div>
                <div class="v2-order-products__breakdown-col">
                  <div class="v2-order-products__breakdown-label v2-order-products__breakdown-label--warning">
                    Remaining Cartons
                  </div>
                  <div class="v2-order-products__breakdown-value v2-order-products__breakdown-value--warning">
                    {{ getRemainingUnits(product) }}
                  </div>
                </div>
              </div>
            </template>
            <!-- Non-carton products: variant tables -->
            <template v-else>
              <div class="v2-order-products__breakdown-section">
                <div class="v2-order-products__breakdown-label v2-order-products__breakdown-label--success">
                  Shipped
                </div>
                <product-variants
                  :disabled-product="true"
                  :is-carton="product.isCarton"
                  :items="getShippedItemsForDisplay(product)"
                  :is-modify-enabled-brand="false"
                />
              </div>
              <div class="v2-order-products__breakdown-section">
                <div class="v2-order-products__breakdown-label v2-order-products__breakdown-label--warning">
                  Remaining
                </div>
                <product-variants
                  :disabled-product="true"
                  :is-carton="product.isCarton"
                  :items="getRemainingItemsForDisplay(product)"
                  :is-modify-enabled-brand="false"
                />
              </div>
            </template>
          </div>
        </div>
      </template>

      <!-- Empty state -->
      <template v-else>
        <v2-card class="v2-text-center v2-p-6">
          {{ emptyProductsText }}
        </v2-card>
      </template>
    </template>

    <!-- Loading -->
    <template v-else>
      <div class="v2-flex v2-justify-center v2-p-8">
        <v2-spinner label="Loading products..." />
      </div>
    </template>

    <!-- Remove product modal -->
    <v2-modal
      :show="!!showRemoveModal"
      title="Remove this product?"
      @close="showRemoveModal = null"
    >
      <p>Are you sure you want to delete this product?</p>
      <template #footer>
        <v2-button
          variant="outline"
          @click="showRemoveModal = null"
        >
          Dismiss
        </v2-button>
        <v2-button
          variant="destructive"
          @click="handleRemoveProduct"
        >
          Remove product
        </v2-button>
      </template>
    </v2-modal>

    <v2-modal
      :show="showReturnModal"
      :title="isAllSelectedForReturn
        ? 'This will delete the shipment'
        : 'Move products back to the order?'"
      @close="showReturnModal = false"
    >
      <p>
        The selected {{ selectedForReturnProductIds.length }} product(s) will be removed
        from this shipment and their quantities returned to the order.
      </p>
      <!--
        Removing every product empties the shipment, and the server soft deletes
        it. Deleting is not undoable, so it is never implicit - the bulk wizard
        warns for the same reason and this surface has to as well.
      -->
      <p v-if="isAllSelectedForReturn">
        <strong>
          That is every product on this shipment, so the shipment will be
          deleted.
        </strong>
      </p>
      <template #footer>
        <v2-button
          variant="outline"
          @click="showReturnModal = false"
        >
          Dismiss
        </v2-button>
        <v2-button
          :variant="isAllSelectedForReturn ? 'destructive' : 'primary'"
          @click="returnSelectedProductsToOrder"
        >
          {{ isAllSelectedForReturn ? 'Remove and delete' : 'Move to Order' }}
        </v2-button>
      </template>
    </v2-modal>

    <!-- Product Detail Modal (V2) -->
    <V2ProductDetailModal
      v-if="modalProductId"
      :visible="!!modalProductId"
      :product-id="modalProductId"
      :collection-id="order && order.collectionId"
      @close="onCloseProductModal"
      @deleted="onCloseProductModal"
    />
  </div>
</template>

<script>
import { onMounted, onUnmounted } from '@vue/composition-api'
import V2Card from '@/components/v2/V2Card.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Badge from '@/components/v2/V2Badge.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2ProductDetailModal from '@/views/v2/product/V2ProductDetailModal.vue'
import ProductVariants from '@/views/apps/product/detail/ProductVariants.vue'
import { useOrderProducts } from '@/composables/useOrderProducts'
import { getShipSelectAllLabel } from '@/views/apps/order/order-utils'
import { useOrderPartialConfirm } from '@/composables/useOrderPartialConfirm'
import { filterOrderProductsBySearch } from '@/views/apps/order/order-product-search'
import { debounce } from 'lodash'
import UserRoleMixin from '@/views/apps/UserRoleMixin.vue'
import { constants as c } from '@kingpin-global/kingpin-utils-frontend'
import store from '@/store'
import { RETURN_PRODUCTS_TO_ORDER } from '@/store/modules/shipment.module'
import { apiToastSuccess, apiToastErrorV2 } from '@/@core/utils/toast'

const { ORDER_STATUS } = c

export default {
  name: 'V2OrderProducts',
  components: {
    V2Card,
    V2Button,
    V2Badge,
    V2Spinner,
    V2Modal,
    V2Input,
    V2ProductDetailModal,
    ProductVariants,
  },
  mixins: [UserRoleMixin],
  props: {
    order: {
      type: Object,
      default: () => null,
    },
    orderProducts: {
      type: Array,
      default: () => null,
    },
    isShipmentView: {
      type: Boolean,
      default: false,
    },
    isShipmentSettingsModal: {
      type: Boolean,
      default: false,
    },
    isModifyEnabledProp: {
      type: Boolean,
      default: false,
    },
    isFilterApplied: {
      type: Boolean,
      default: false,
    },
    isReturnToOrderEnabled: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const products = useOrderProducts()

    onMounted(() => {
      window.addEventListener('scroll', products.onScrollLoader)
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', products.onScrollLoader)
    })

    if (props.orderProducts?.length) {
      products.setOrderProducts(props.orderProducts, props.order)
      products.initProductDiscounts(props.orderProducts)
    }

    const partialConfirm = useOrderPartialConfirm()

    return { ...products, ...partialConfirm, getShipSelectAllLabel }
  },
  data() {
    return {
      showRemoveModal: null,
      modalProductId: null,
      selectedForReturn: {},
      isReturningProductsToOrder: false,
      showReturnModal: false,
      productSearch: '',
    }
  },
  computed: {
    isProductSearchEnabled() {
      return !this.isShipmentView && !!(this.orderProducts || []).length
    },
    // KP-4363: the products the search leaves visible ("select all" acts on these).
    visibleOrderProducts() {
      return filterOrderProductsBySearch(this.orderProducts || [], this.productSearchQuery)
    },
    emptyProductsText() {
      if (this.productSearchQuery) return 'No products match your search'
      return this.isFilterApplied ? 'No products found. Try a new filter.' : 'No products in your order'
    },
    selectedForReturnProductIds() {
      return Object.keys(this.selectedForReturn).filter(
        productId => this.selectedForReturn[productId],
      )
    },
    hasSelectedForReturn() {
      return this.selectedForReturnProductIds.length > 0
    },
    isAllSelectedForReturn() {
      const eligibleProducts = (this.orderProducts || []).filter(
        product => !this.isProductDeletedOrCancelled(product),
      )
      return eligibleProducts.length > 0
        && eligibleProducts.every(product => this.selectedForReturn[product.productId])
    },
    productCurrency() {
      if (!this.order) return ''
      return this.isBrand ? this.order.currencyForBrand : this.order.currency
    },
    isDiscountEnabled() {
      if (!this.order) return false
      return this.isBrand
        && this.order.status !== ORDER_STATUS.CANCELLED
        && this.order.status !== ORDER_STATUS.REJECTED
        && !this.isShipmentView
    },
    hasPendingProductsToConfirm() {
      return this.isPartialConfirmEnabled(this.order, this.isShipmentView)
        && (this.orderProducts || []).some(this.isPendingConfirmLine)
    },
  },
  watch: {
    orderProducts: {
      handler(newVal, oldVal) {
        this.setOrderProducts(newVal, this.order)
        this.initProductDiscounts(newVal)
        // Reset return-to-order selection so a filter change can't leave
        // previously-selected, now-hidden products silently included in the
        // next "Move to Order" submission.
        this.selectedForReturn = {}
        // Same for the KP-4363 confirm selection, but only for a new list: a
        // ship tick changes the same list in place and must not drop it.
        if (newVal !== oldVal) this.clearConfirmSelection()
      },
    },
    productSearch(query) {
      this.debouncedSearchOrderProducts(query)
    },
    selectedProductIds: {
      handler() {
        const confirmIds = this.getSelectedForConfirmIds(this.orderProducts)
        // KP-4363: confirming and shipping are separate selections; picking
        // products to confirm drops the products picked for shipping.
        if (confirmIds.length && this.isSelectedProduct) {
          this.onSelectReadyToShipProducts(false, this.order)
        }
        this.$emit('partial-confirm-selection', confirmIds)
      },
      deep: true,
      immediate: true,
    },
    isSelectedProduct(isSelected) {
      // KP-4363: picking products to ship drops the products picked to confirm.
      if (isSelected) this.clearConfirmSelection()
    },
  },
  created() {
    this.debouncedSearchOrderProducts = debounce(query => this.searchOrderProducts(query), 200)
  },
  beforeDestroy() {
    this.debouncedSearchOrderProducts.cancel()
  },
  methods: {
    isSelectedForReturn(product) {
      return !!this.selectedForReturn[product.productId]
    },
    onToggleReturnSelection(isSelected, product) {
      this.$set(this.selectedForReturn, product.productId, isSelected)
    },
    onToggleSelectAllForReturn(isSelected) {
      (this.orderProducts || []).forEach(product => {
        if (!this.isProductDeletedOrCancelled(product)) {
          this.$set(this.selectedForReturn, product.productId, isSelected)
        }
      })
    },
    // 409 means the shipment changed under us - already shipped out, deleted, or
    // another request removed the same products. Nothing was changed, and a
    // retry against this now-stale list would be wrong, so reload instead of
    // leaving the button armed.
    onReturnFailed(error) {
      const response = error && error.response
      if (response && response.status === 409) {
        const message = (response.data && response.data.message)
          || 'This shipment changed while the request was in flight. Refreshing.'
        apiToastErrorV2(message)
        this.$emit('load-shipment')
        return
      }
      apiToastErrorV2(error)
      this.isReturningProductsToOrder = false
    },
    async returnSelectedProductsToOrder() {
      const productIds = this.selectedForReturnProductIds
      if (!productIds.length) {
        return
      }
      this.isReturningProductsToOrder = true
      this.showReturnModal = false
      try {
        const res = await store.dispatch(RETURN_PRODUCTS_TO_ORDER, {
          shipmentId: this.order._id,
          productIds,
        })
        apiToastSuccess(res.data.message || 'Products moved back to the order successfully!')
        // Removing the last product soft deletes the shipment, and every read
        // path filters those out - re-fetching it would 404 into a swallowed
        // catch and leave this page frozen on a shipment that no longer exists.
        // Go back to the list instead.
        if (res.data.data && res.data.data.shipmentDeleted) {
          this.$router.push({ name: 'v2-shipments' })
          return
        }
        // Leave isReturningProductsToOrder (and the selection) as-is on success: the
        // button stays disabled and the stale product list stays non-resubmittable
        // until `load-shipment` completes and remounts this component with fresh data.
        this.$emit('load-shipment')
      } catch (error) {
        this.onReturnFailed(error)
      }
    },
    emitShipNow(event, data) {
      this.$emit('ship-now', data)
    },
    handleRemoveProduct() {
      const productId = this.showRemoveModal
      const index = this.productsSlice.findIndex(p => p.productId === productId)
      const product = this.productsSlice.find(p => p.productId === productId)
      if (product) {
        this.$emit('remove-product', product, index)
      }
      this.showRemoveModal = null
    },
    handleApplyDiscount(product) {
      this.applyProductDiscount(product, this.order._id, this.$emit.bind(this))
    },
    getStatusBadgeVariant(status) {
      const result = this.getStatusTextAndColorCode(status, this.order)
      if (!result?.colorCode) return 'default'
      const colorMap = {
        '#28c76f': 'success',
        '#ea5455': 'destructive',
        '#ff9f43': 'warning',
        '#00cfe8': 'primary',
        '#7367f0': 'primary',
        '#1f9d55': 'success',
      }
      return colorMap[result.colorCode] || 'default'
    },
    onClickProduct(product) {
      if (!product?.productId) return
      this.modalProductId = product.productId
    },
    onCloseProductModal() {
      this.modalProductId = null
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/v2/_glass-mixins.scss';

.v2-order-products {
  &__search {
    max-width: 360px;
    margin-bottom: 1rem;
  }

  &__ship-controls {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  &__select-all {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    cursor: pointer;

    input {
      accent-color: var(--v2-primary);
    }
  }

  &__card {
    // Canonical no-backdrop-filter glass — translucent fill + sheen
    // gradient + glass-stroke + compound shadow (mixin in
    // _glass-mixins.scss). 50+ rows render per page; page mesh provides
    // the optical "frosted" effect once for the viewport. `contain:
    // layout` lets the browser skip layout work on off-screen cards.
    @include v2-glass-panel-flat;
    border-radius: 0.625rem;
    margin-bottom: 1rem;
    overflow: hidden;
    contain: layout;

    &--selected {
      border-color: var(--v2-primary);
    }

    &--error {
      border-color: var(--v2-destructive);
    }

    &--discount {
      border-left: 3px solid var(--v2-success);
    }
  }

  &__layout {
    display: grid;
    grid-template-columns: 200px 1fr 200px;
    gap: 1.25rem;
    padding: 0 1.25rem 0 0;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      padding: 1.25rem;
    }
  }

  &__image {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    aspect-ratio: 2/3;
    overflow: hidden;
  }

  &__image-trigger {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--v2-primary);
      outline-offset: -2px;
    }
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    border-radius: 0.625rem 0 0 0.625rem;
  }

  // Banner matches the image width so the bottom edge of the card looks
  // continuous (was calc(100% - 0.875rem) which left a narrow gap and
  // misaligned with the image edges).
  &__removed-label,
  &__modified-label {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    font-size: 0.6875rem;
    text-align: center;
    padding: 4px 6px;
    border-bottom-left-radius: 0.625rem;
  }

  &__removed-label {
    color: var(--v2-destructive);
    background: rgba(229, 72, 77, 0.1);
  }

  &__modified-label {
    color: var(--v2-warning);
    background: rgba(245, 158, 11, 0.1);
  }

  &__info {
    flex: 1;
    min-width: 0;
    padding-top: 1.25rem;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  &__name {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin: 0;
  }

  &__name-link {
    color: inherit;
    text-decoration: none;
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    text-align: left;
    cursor: pointer;

    &:hover {
      color: var(--v2-primary);
    }

    &:focus-visible {
      outline: 2px solid var(--v2-primary);
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  &__breadcrumb {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    margin: 0.25rem 0 0;
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__detail-row {
    font-size: 0.8125rem;
    color: var(--v2-muted-foreground);
    display: flex;
    align-items: center;
    gap: 0.25rem;

    strong {
      color: var(--v2-foreground);
    }
  }

  &__discounted {
    color: var(--v2-success) !important;
  }

  // Discount editing section
  &__discount-section {
    margin-top: 0.5rem;
    font-size: 0.8125rem;
    color: var(--v2-muted-foreground);
  }

  &__discount-view {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__discount-edit-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.125rem;
    background: none;
    border: none;
    color: var(--v2-muted-foreground);
    cursor: pointer;
    border-radius: 0.25rem;

    &:hover {
      color: var(--v2-primary);
      background: var(--v2-muted);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__discount-edit {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  &__discount-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &__discount-input {
    width: 80px;
    padding: 0.25rem 0.5rem;
    border: 1px solid var(--v2-border);
    border-radius: 0.375rem;
    font-size: 0.8125rem;
    text-align: center;

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__discount-hint {
    color: var(--v2-muted-foreground);
    font-size: 0.75rem;
  }

  &__carton-qty {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    font-size: 0.8125rem;
    flex-wrap: wrap;
  }

  &__carton-pending {
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    width: 100%;
    margin-top: 0.125rem;
  }

  &__qty-input {
    width: 80px;
    padding: 0.25rem 0.5rem;
    border: 1px solid var(--v2-border);
    border-radius: 0.375rem;
    font-size: 0.8125rem;
    text-align: center;

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &--invalid {
      border-color: var(--v2-destructive) !important;
      border-width: 2px;
    }
  }

  &__totals-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    border-left: 1px solid var(--v2-border);
    padding: 1.25rem 0 1.25rem 1.25rem;
    min-width: 200px;

    @media (max-width: 768px) {
      border-left: none;
      border-top: 1px solid var(--v2-border);
      padding: 1rem 0;
    }
  }

  &__totals-actions {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  &__ship-checkbox {
    cursor: pointer;

    input {
      width: 18px;
      height: 18px;
      accent-color: var(--v2-primary);
    }
  }

  &__remove-btn {
    width: 75%;
    margin-bottom: 2rem;
  }

  &__totals {
    text-align: center;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &__total-value {
    font-size: 1rem;
    font-weight: 700;
    color: var(--v2-foreground);
    margin-bottom: 0.5rem;
  }

  &__total-units {
    font-size: 0.8125rem;
    color: var(--v2-muted-foreground);
  }

  &__partial-bar {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    border-top: 1px solid var(--v2-border);
    cursor: pointer;
    background: var(--v2-muted);

    &:hover {
      background: var(--v2-border);
    }
  }

  &__expand-text {
    font-size: 0.8125rem;
    color: var(--v2-primary);
    font-weight: 500;
  }

  // Partial shipment breakdown
  &__breakdown {
    border-top: 1px solid var(--v2-border);
    margin: 0 1rem 1rem;
    padding: 1rem;
    border-radius: 0 0 0.5rem 0.5rem;
  }

  &__breakdown-cartons {
    display: flex;
    justify-content: space-around;
    padding: 0.75rem 0;
  }

  &__breakdown-col {
    text-align: center;
  }

  &__breakdown-label {
    font-size: 0.8125rem;
    font-weight: 600;
    margin-bottom: 0.375rem;

    &--success {
      color: var(--v2-success);
    }

    &--warning {
      color: var(--v2-warning);
    }
  }

  &__breakdown-value {
    font-size: 1.25rem;
    font-weight: 700;

    &--success {
      color: var(--v2-success);
    }

    &--warning {
      color: var(--v2-warning);
    }
  }

  &__breakdown-section {
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  // ProductVariants b-table overrides now live in _components.scss (global)
}
</style>
